import { HttpParams } from '@angular/common/http';
import { Component, HostListener, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SoundType } from '../../../../classes/sound-type';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { AppService } from '../../../../services/app/app.service';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { GruppeTierReklamierenDialogComponent } from '../../../gruppe-tier/gruppe-tier-reklamieren-dialog/gruppe-tier-reklamieren-dialog.component';
import { Haendler } from '../../../stammdaten/haendler/haendler';
import { Gruppe } from '../../classes/gruppe';
import { ScanErgebnis } from '../../classes/scan-ergebnis';
import { GruppenService } from '../../gruppen.service';
import { GewichtEingebenNumberpadDialogComponent } from '../einstallen-tiere-hinzufuegen/gewicht-eingeben-numberpad-dialog/gewicht-eingeben-numberpad-dialog.component';
import { TransponderNummerEingebenDialogComponent } from '../transponder-nummer-eingeben-dialog/transponder-nummer-eingeben-dialog.component';
import { EinstallenService } from '../einstallen.service';
import {
    hasOhrmarkeScanned,
    hasPassScanned,
    isComplementScanSuccess,
    isSameTierScan,
    isDuplicateScanType,
    isValidOhrmarkePassScanLength,
    mergeScanErgebnisse,
    normalizeOhrmarkePassScanInput,
    OHRMARKE_SCAN_LENGTH,
    OHRMARKE_WITH_CHECK_DIGITS_LENGTH,
    OhrmarkeScanTrailGuard,
} from '../../../../utils/ohrmarke-pass-scan.util';

@Component({
    selector: 'kiene-einstallen-number-pad',
    templateUrl: './einstallen-number-pad.component.html',
    styleUrls: ['./einstallen-number-pad.component.scss'],
})
export class EinstallenNumberPadComponent implements OnInit {
    readonly hasPassScanned = hasPassScanned;
    readonly hasOhrmarkeScanned = hasOhrmarkeScanned;
    gruppe?: Gruppe;

    numbers = '';
    private readonly trailGuard = new OhrmarkeScanTrailGuard();
    fontSize = '3em';
    checkBackgroundColor = 'lightgray';
    checkColor = 'gray';
    checkBorderColor = 'black'; //"transparent";
    ohrmarke = '276031234567890';
    ohrmarkeGefunden = false;
    currentHaendler: Haendler;

    ohrmarken: GruppeTier[];

    keinErgebnis: boolean;
    selectedTier: GruppeTier;
    abteilCtrl: UntypedFormControl = new UntypedFormControl(null);
    /** Ohrmarke ohne Datum: Pass oft noch nicht da, genaues Alter unbekannt. */
    datumCtrl: UntypedFormControl = new UntypedFormControl(new Date());
    currentScanErgebnis: ScanErgebnis;
    isLoading = false;
    ladeOhrmarkeSub: Subscription;

    mitGewichtToggleCtrl = new UntypedFormControl();
    mitTransponderNummerToggleCtrl = new UntypedFormControl();
    geschlechtCtrl = new UntypedFormControl(null);

    @Input('isVisible') isVisible = false;
    @Input() hidePageHeader = false;
    letzteScans: ScanErgebnis[] = [];
    isTabletLandscape = false;
    private readonly gruppeId: string | null;

    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private einstallService: EinstallenService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private gruppenService: GruppenService,
        private appService: AppService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) {
        this.gruppeId = this.route.snapshot.paramMap.get('gruppeid');
    }

    ngOnInit(): void {
        this.isTabletLandscape = this.getTabletLandscape();
        console.log('getTabletLandscape', this.isTabletLandscape);
        this.einstallService.watchCurrentGruppe().subscribe({ next: val => (this.gruppe = val) });
        this.einstallService.watchCurrentHaendler().subscribe({ next: val => (this.currentHaendler = val) });

        const savedEinstalldatum = this.einstallService.getCurrentEinstalldatum() ?? new Date();
        this.datumCtrl.setValue(savedEinstalldatum, { emitEvent: false });
        this.einstallService.setCurrentEinstalldatum(savedEinstalldatum);

        this.einstallService.watchCurrentAbteil().subscribe({
            next: val => {
                this.abteilCtrl.setValue(val, { emitEvent: false });
            },
        });
        this.abteilCtrl.valueChanges.subscribe(abteil => {
            this.einstallService.setCurrentAbteil(abteil);
        });
        this.datumCtrl.valueChanges.subscribe(value => {
            this.einstallService.setCurrentEinstalldatum(value || null);
        });

        this.einstallService.watchScanErgebnisSubject().subscribe(scanErgebnis => {
            this.currentScanErgebnis = scanErgebnis;
        });
    }

    clearEinstalldatum(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.datumCtrl.setValue(null);
    }

    @HostListener('window:keyup', ['$event'])
    catchKeyboardEvent(event: KeyboardEvent) {
        if (!this.isVisible) {
            return;
        }
        console.log(event.code);
        if ((event.code.startsWith('Numpad') || event.code.startsWith('Digit')) && !this.isKeyEventFromInput(event)) {
            if (this.trailGuard.shouldIgnoreIncoming()) {
                return;
            }
            const splitted = event.code.split(/Numpad|Digit/);
            if (splitted && splitted[1]) {
                this.addNumber(Number.parseInt(splitted[1], 10));
            }
        } else if (event.code === 'Backspace' && !this.isKeyEventFromInput(event)) {
            this.backspace();
        }
    }

    addNumber(number: number) {
        if (this.trailGuard.shouldIgnoreIncoming()) {
            return;
        }
        if (this.numbers.length < OHRMARKE_WITH_CHECK_DIGITS_LENGTH) {
            this.calcFontSize();
            this.numbers += `${number}`;
            // Collapse 12-digit ear-tag barcodes (10 + check) to 10 so check digits never linger.
            const normalized = normalizeOhrmarkePassScanInput(this.numbers);
            if (
                this.numbers.length === OHRMARKE_WITH_CHECK_DIGITS_LENGTH &&
                normalized.length === OHRMARKE_SCAN_LENGTH
            ) {
                this.numbers = normalized;
            }
            this.ladeOhrmarke();
            this.calcFontSize();
        }
    }

    ladeOhrmarke() {
        this.ohrmarken = [];
        this.checkOhrmarken();
        if (this.numbers.length >= 5) {
            this.isLoading = true;
            let params = new HttpParams();
            params = params.set('gruppe_id', this.gruppe.gruppe_id.toString());
            params = params.set('ohrmarke5', this.numbers);

            this.ladeOhrmarkeSub?.unsubscribe();
            this.ladeOhrmarkeSub = this.api
                .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
                .subscribe({
                    next: response => {
                        this.isLoading = false;
                        this.ohrmarken = response.records;
                        this.checkOhrmarken();
                    },
                    error: error => {
                        this.isLoading = false;
                        this.messageService.errorMessage('Fehler bei der Ohrmarkensuche!', error);
                    },
                });
        }
    }

    backspace() {
        if (this.numbers.length >= 1) {
            this.numbers = this.numbers.substring(0, this.numbers.length - 1);
            this.ladeOhrmarke();
        }

        this.calcFontSize();
    }

    checkOhrmarken() {
        if (this.ohrmarken.length === 1) {
            this.selectedTier = this.ohrmarken[0];
            this.ohrmarke = this.ohrmarken[0].ohrmarke_notnull;
            this.ohrmarkeGefunden = true;
            this.keinErgebnis = false;
            this.checkBackgroundColor = 'green';
            this.checkColor = 'white';
            this.checkBorderColor = 'green';
        } else if (this.ohrmarken.length === 0) {
            this.selectedTier = undefined;
            this.ohrmarke = this.numbers.toString();
            this.ohrmarkeGefunden = false;
            this.keinErgebnis = true;
            this.checkBackgroundColor = this.numbers.length < 10 ? 'lightgray' : 'green';
            this.checkColor = this.numbers.length < 10 ? 'gray' : 'white';
            this.checkBorderColor = 'black'; //"transparent";
        } else {
            // Mehrere Ohrmarken gefunden
            this.selectedTier = undefined;
            this.ohrmarke = 'nicht vorhanden';
            this.ohrmarkeGefunden = false;
            this.keinErgebnis = true;
            this.checkBackgroundColor = 'lightgray';
            this.checkColor = 'gray';
            this.checkBorderColor = 'black'; //"transparent";
        }
    }

    ohrmarkeUebernehmen(tier: GruppeTier) {
        // Die vollständige Ohrmarke übernehmen
        const ohrmarke = tier.ohrmarke_notnull || tier.ohrmarke || tier.ohrmarke15 || tier.ohrmarke_kurz || '';
        // Nur die Ziffern extrahieren
        const ziffern = ohrmarke.replace(/\D/g, '');
        this.numbers = ziffern;
        this.calcFontSize();
        // Erneut nach Ohrmarken suchen mit der vollständigen Nummer
        this.ladeOhrmarke();
    }

    clearAll() {
        this.numbers = '';
        this.trailGuard.armTrailingIgnore();
        this.selectedTier = null;
        this.checkBackgroundColor = 'lightgray';
        this.checkColor = 'gray';
        this.checkBorderColor = 'black'; //"transparent";
        this.ohrmarkeGefunden = false;
        this.keinErgebnis = true;
        this.ohrmarke = '';
        this.ohrmarken = [];
        this.geschlechtCtrl.setValue(null);
    }

    check() {
        this.trailGuard.settleThenRun(
            () => this.numbers,
            () => this.submitNumbers(),
        );
    }

    private submitNumbers() {
        const normalizedNumbers = normalizeOhrmarkePassScanInput(this.numbers);
        let tier: GruppeTier;

        if (this.selectedTier) {
            tier = this.selectedTier;
        } else {
            tier = new GruppeTier();
            tier.ohrmarke = normalizedNumbers;
        }

        tier.gruppe_id = this.gruppe.gruppe_id;
        if (this.gruppe?.kunde_id) {
            tier.kunde_id = this.gruppe.kunde_id;
        }
        const einstallTierlisteId = this.einstallService.getCurrentEinstallTierlisteId();
        if (einstallTierlisteId) {
            (tier as { einstall_tierliste_id?: number }).einstall_tierliste_id = einstallTierlisteId;
        }
        tier.abteil = this.einstallService.resolveAbteil(this.abteilCtrl.value);

        const betriebskontaktHaendlerId = this.einstallService.getCurrentBetriebskontaktHaendlerId();
        if (betriebskontaktHaendlerId) {
            tier.betriebskontakt_haendler_id = betriebskontaktHaendlerId;
        } else if (this.currentHaendler?.viehhaendler_id) {
            tier.viehhaendler_id = this.currentHaendler.viehhaendler_id;
        }

        const einstalldatum = this.einstallService.getCurrentEinstalldatum();
        if (einstalldatum) {
            tier.kp_einstalldatum = einstalldatum;
        }

        // Geschlecht setzen:
        // - Bei 10-stelligen Ohrmarken (manuelle Eingabe) immer
        // - Bei 5-stelligen Ohrmarken nur wenn eine Ohrmarke ausgewählt wurde oder kein Ergebnis gefunden wurde
        if (this.geschlechtCtrl.value !== null) {
            if (normalizedNumbers.length === OHRMARKE_SCAN_LENGTH) {
                // Immer bei 10-stelligen Ohrmarken
                tier.geschlecht_id = parseInt(this.geschlechtCtrl.value);
            } else if (this.numbers.length === 5 && (this.ohrmarkeGefunden || this.keinErgebnis)) {
                // Bei 5-stelligen nur wenn eine Ohrmarke gefunden wurde oder kein Ergebnis
                tier.geschlecht_id = parseInt(this.geschlechtCtrl.value);
            }
        }

        this.promptOptionalScanFieldsThenPost(tier);
    }

    changeTransponderNrToggle(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.mitTransponderNummerToggleCtrl.setValue(!this.mitTransponderNummerToggleCtrl.value);
    }

    /** Optional: Gewicht- und/oder Transponder-Dialog, dann Speichern (wie Desktop-Einstallen). */
    private promptOptionalScanFieldsThenPost(tier: GruppeTier) {
        this.applyExistingScanValues(tier);
        const afterGewicht = () => {
            if (this.mitTransponderNummerToggleCtrl.value) {
                this.dialog
                    .open(TransponderNummerEingebenDialogComponent, {
                        data: { transpondernummer: tier.transpondernummer },
                    })
                    .afterClosed()
                    .subscribe(transponderNummer => {
                        if (transponderNummer) {
                            tier.transpondernummer = transponderNummer;
                        }
                        this.postOhrmarke(tier);
                    });
                return;
            }
            this.postOhrmarke(tier);
        };

        if (this.mitGewichtToggleCtrl.value) {
            this.dialog
                .open(GewichtEingebenNumberpadDialogComponent, {
                    data: { gewicht: tier.einstallgewicht },
                })
                .afterClosed()
                .subscribe(result => {
                    if (result) {
                        tier.einstallgewicht = result;
                    }
                    afterGewicht();
                });
            return;
        }

        afterGewicht();
    }

    private applyExistingScanValues(tier: GruppeTier): void {
        const scanned = String(tier?.ohrmarke || '').trim();
        if (!scanned) {
            return;
        }
        const existing = (this.letzteScans || []).find(
            scan => !!scan?.success && isSameTierScan(scanned, scan.ohrmarke_notnull),
        );
        if (!existing) {
            return;
        }
        if (!tier.einstallgewicht && existing.einstallgewicht) {
            tier.einstallgewicht = existing.einstallgewicht;
        }
        if (!tier.transpondernummer && existing.transpondernummer) {
            tier.transpondernummer = existing.transpondernummer;
        }
        if (existing.abteil) {
            tier.abteil = existing.abteil;
        }
    }

    postOhrmarke(tier) {
        this.appService.setBlockOnLoading(true);
        this.api.post(this.apiBaseUrl + 'gruppe_tier/create_kundenportal.php', tier, undefined).subscribe({
            next: (response: ScanErgebnis) => {
                response.success = true;
                const scannedValue = String(tier?.ohrmarke || '').trim();
                const apiBereitsInGruppe = !!response.bereits_in_gruppe || !!response.bereitsInGruppe;
                const isComplement = scannedValue && isComplementScanSuccess(response, scannedValue);
                response.bereitsInGruppe = apiBereitsInGruppe && !isComplement;

                if (response.bereitsInGruppe && response.gt_id) {
                    this.einstallService.registerBereitsInGruppeGtIds([response.gt_id]);
                }

                const isDuplicate = scannedValue && isDuplicateScanType(response, scannedValue);

                if (response.bereitsInGruppe) {
                    this.messageService.hintMessage(this.buildBereitsInGruppePopupText(response));
                }

                this.appendScanErgebnis(response);
                this.gruppenService.playScanSound(
                    response.bereitsInGruppe || isDuplicate ? SoundType.WARNING : SoundType.OK,
                );
                this.einstallService.setScanErgebnisSubject(response);
                this.einstallService.addScanErgebnis(response);
                this.clearAll();
                this.appService.setBlockOnLoading(false);
            },
            error: err => {
                this.gruppenService.playScanSound(SoundType.ERROR);
                this.messageService.errorMessage('Fehler', err);
                this.appService.setBlockOnLoading(false);
            },
        });
    }

    calcFontSize() {
        if (this.numbers.length > 12) {
            this.fontSize = '1.2em';
        } else if (this.numbers.length > 8) {
            this.fontSize = '2em';
        } else {
            this.fontSize = '3em';
        }
    }

    get hasFullOhrmarkeInput(): boolean {
        return normalizeOhrmarkePassScanInput(this.numbers).length === OHRMARKE_SCAN_LENGTH;
    }

    get canSubmitNumbers(): boolean {
        return isValidOhrmarkePassScanLength(this.numbers);
    }

    getFirstTenDigits(ohrmarke: string) {
        return ohrmarke.substring(0, ohrmarke.length - 5);
    }

    getLastFiveDigits(ohrmarke: string) {
        return ohrmarke.substring(ohrmarke.length - 5, ohrmarke.length);
    }

    get passGescanntDisplay(): string {
        return String(this.countPassGescannt(this.currentScanErgebnis));
    }

    get ohrmarkeGescanntDisplay(): string {
        return String(this.countOhrmarkeGescannt(this.currentScanErgebnis));
    }

    private countPassGescannt(source?: Pick<ScanErgebnis, 'paesse_ohne_tier' | 'anzahl_tiere_match'>): number {
        return (Number(source?.paesse_ohne_tier) || 0) + (Number(source?.anzahl_tiere_match) || 0);
    }

    private countOhrmarkeGescannt(source?: Pick<ScanErgebnis, 'tiere_ohne_pass' | 'anzahl_tiere_match'>): number {
        return (Number(source?.tiere_ohne_pass) || 0) + (Number(source?.anzahl_tiere_match) || 0);
    }

    canReklamierenScan(scan: ScanErgebnis): boolean {
        return !!scan?.success && Number(scan?.gt_id) > 0;
    }

    hasTranspondernummer(scan: ScanErgebnis): boolean {
        return this.canReklamierenScan(scan) && !!String(scan?.transpondernummer || '').trim();
    }

    reklamationFuerScanHinzufuegen(scanResult: ScanErgebnis) {
        if (!this.canReklamierenScan(scanResult)) {
            return;
        }

        const gruppeId = Number(this.gruppeId || 0);
        if (gruppeId > 0) {
            this.router
                .navigate(['/gruppen/tierehinzufuegen', gruppeId, 'tierreklamation', scanResult.gt_id])
                .then();
            return;
        }

        const tier = new GruppeTier();
        tier.gt_id = scanResult.gt_id;
        tier.ohrmarke_notnull = scanResult.ohrmarke_notnull;

        this.dialog
            .open(GruppeTierReklamierenDialogComponent, {
                width: '760px',
                maxWidth: '95vw',
                maxHeight: '85vh',
                autoFocus: false,
                data: { tier: tier },
            })
            .afterClosed()
            .subscribe(savedTier => {
                if (!savedTier) {
                    return;
                }
                scanResult.hat_tierreklamation = savedTier.reklamationen?.length ? 1 : 0;
            });
    }

    transponderFuerScanBearbeiten(scanResult: ScanErgebnis) {
        if (!this.hasTranspondernummer(scanResult)) {
            return;
        }

        this.dialog
            .open(TransponderNummerEingebenDialogComponent, {
                data: { transpondernummer: scanResult.transpondernummer },
            })
            .afterClosed()
            .subscribe(value => {
                const next = String(value || '').trim();
                if (!next || next === String(scanResult.transpondernummer || '').trim()) {
                    return;
                }
                this.einstallService.updateTranspondernummer(scanResult.gt_id, next).subscribe({
                    next: () => {
                        scanResult.transpondernummer = next;
                        this.messageService.infoMessage('Transpondernummer gespeichert.');
                    },
                    error: err => {
                        this.messageService.errorMessage('Transpondernummer konnte nicht gespeichert werden!', err);
                    },
                });
            });
    }

    private isKeyEventFromInput(event: KeyboardEvent): boolean {
        if (event.target instanceof Node) {
            switch (event.target.nodeName) {
                case 'INPUT':
                case 'SELECT':
                case 'TEXTAREA':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }

    private getTabletLandscape(): boolean {
        if (this.appService.isMobileScreen) {
            return false;
        } else if (this.appService.isTablet) {
            if (this.appService.isPortrait) {
                return false;
            }
            return true;
        }
        return true;
    }

    private buildBereitsInGruppePopupText(scan: ScanErgebnis): string {
        const ohrmarke = String(scan?.ohrmarke_notnull || '').trim();
        if (ohrmarke) {
            return `Tier ${ohrmarke} ist bereits in dieser Gruppe.`;
        }
        return 'Tier ist bereits in dieser Gruppe.';
    }

    private appendScanErgebnis(scanErgebnis: ScanErgebnis): void {
        const se = scanErgebnis;
        se.success = true;

        const gtId = Number(se.gt_id || 0);
        if (gtId > 0) {
            const existingIndex = this.letzteScans.findIndex(scan => Number(scan?.gt_id) === gtId);
            if (existingIndex >= 0) {
                const merged = mergeScanErgebnisse(this.letzteScans[existingIndex], se);
                this.letzteScans.splice(existingIndex, 1);
                this.letzteScans.unshift(merged);
                if (this.letzteScans.length > 10) {
                    this.letzteScans.splice(10);
                }
                return;
            }
        }

        this.letzteScans.unshift(se);
        if (this.letzteScans?.length > 10) {
            this.letzteScans.splice(10, this.letzteScans?.length);
        }
    }
}
